import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGetLead, apiCreateLead, apiUpdateLead, apiGetCounselors } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUSES = ['New', 'Contacted', 'Follow-up', 'Will Visit', 'Visited', 'Converted', 'Not Interested'];
const SOURCES  = ['Website', 'Facebook Ads', 'Instagram Ads', 'Walk-in', 'Referral'];
const COURSES  = [
    'Full Stack Web Development', 'Data Science & ML', 'Python Programming',
    'Java Development', 'Digital Marketing', 'UI/UX Design',
    'Cloud Computing (AWS)', 'Cybersecurity', 'DevOps Engineering', 'Other',
];

const empty = {
    name: '', phone: '', email: '', course: '', source: 'Website',
    status: 'New', counselor_id: '', notes: '',
};

export default function LeadFormPage() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const navigate = useNavigate();
    const { user } = useAuth();

    const [form, setForm] = useState(empty);
    const [counselors, setCounselors] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (user?.role === 'admin') {
            apiGetCounselors().then(r => setCounselors(r.data?.data || [])).catch(() => {});
        }
        if (isEdit) {
            apiGetLead(id)
                .then(r => {
                    const l = r.data.data;
                    setForm({
                        name: l.name, phone: l.phone, email: l.email || '',
                        course: l.course, source: l.source, status: l.status,
                        counselor_id: l.counselor_id || '',
                        notes: l.notes || '',
                    });
                })
                .catch(() => toast.error('Lead not found'))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, user]);

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = 'Name is required';
        if (!form.phone.trim()) errs.phone = 'Phone is required';
        if (form.phone && !/^[\d\s\+\-\(\)]{7,15}$/.test(form.phone)) errs.phone = 'Invalid phone number';
        if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Invalid email address';
        if (!form.course.trim()) errs.course = 'Course is required';
        return errs;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(er => ({ ...er, [name]: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setErrors(errs); return; }

        setSaving(true);
        try {
            const payload = { ...form, counselor_id: form.counselor_id || null };
            if (isEdit) {
                await apiUpdateLead(id, payload);
                toast.success('Lead updated successfully!');
                navigate(`/leads/${id}`);
            } else {
                const res = await apiCreateLead(payload);
                toast.success('Lead created successfully!');
                navigate(`/leads/${res.data.id}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-center" style={{ minHeight: '80vh' }}><div className="spinner" /><span>Loading…</span></div>;

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? '✏️ Edit Lead' : '➕ New Lead'}</h1>
                    <p className="page-subtitle">{isEdit ? 'Update the lead details below' : 'Fill in the details to create a new lead'}</p>
                </div>
                <button className="btn btn-ghost" onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}>
                    ← Back
                </button>
            </div>

            <div className="card">
                <div className="card-header">
                    <span className="card-title">Lead Information</span>
                </div>
                <div className="card-body">
                    <form id="lead-form" onSubmit={handleSubmit} noValidate>
                        {/* Row 1 */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="lead-name">Full Name *</label>
                                <input id="lead-name" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Ravi Kumar" />
                                {errors.name && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{errors.name}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="lead-phone">Phone Number *</label>
                                <input id="lead-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="e.g. +91 98765 43210" />
                                {errors.phone && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{errors.phone}</span>}
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="lead-email">Email Address</label>
                                <input id="lead-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="e.g. ravi@email.com" />
                                {errors.email && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{errors.email}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="lead-course">Course of Interest *</label>
                                <select id="lead-course" name="course" value={form.course} onChange={handleChange}>
                                    <option value="">— Select Course —</option>
                                    {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                {errors.course && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{errors.course}</span>}
                            </div>
                        </div>

                        {/* Row 3 */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="lead-source">Lead Source</label>
                                <select id="lead-source" name="source" value={form.source} onChange={handleChange}>
                                    {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor="lead-status">Status</label>
                                <select id="lead-status" name="status" value={form.status} onChange={handleChange}>
                                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Counselor — admin only */}
                        {user?.role === 'admin' && (
                            <div className="form-group">
                                <label htmlFor="lead-counselor">Assign Counselor</label>
                                <select id="lead-counselor" name="counselor_id" value={form.counselor_id} onChange={handleChange}>
                                    <option value="">— Unassigned —</option>
                                    {counselors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Notes */}
                        <div className="form-group">
                            <label htmlFor="lead-notes">Notes</label>
                            <textarea id="lead-notes" name="notes" value={form.notes} onChange={handleChange} placeholder="Any additional information about this lead…" rows={4} />
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn btn-ghost" onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}>Cancel</button>
                            <button id="lead-save-btn" type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</> : `💾 ${isEdit ? 'Update Lead' : 'Create Lead'}`}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
